import { Fragment, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { match } from 'ts-pattern';
import { type Interaction, Interactive } from '../../util/interaction';
import { HorizontalStack, type HorizontalStackProps } from '../alignment/horizontal-stack';
import type { ButtonVariant } from '../button';
import { Button } from '../button/button';
import { TextButton } from '../link/text-link/text-button';
import { Typography, type TypographyTone } from '../typography';
import styles from './status-bar.module.scss';

export type StatusBarVariant = 'text' | 'neutral' | 'primary' | 'secondary' | 'tertiary' | 'danger';

const toneByVariant: Record<StatusBarVariant, TypographyTone> = {
  text: 'background',
  neutral: 'md',
  primary: 'primary-dk',
  secondary: 'secondary-dk',
  tertiary: 'tertiary-dk',
  danger: 'danger-dk',
};

export type StatusBarAction =
  | (Interaction & {
      id: string;
      ctaText?: string;
      ctaIcon?: ReactNode;
      triggerStyle: 'button' | 'link';
      variant?: ButtonVariant;
    })
  | {
      id: string;
      node: ReactNode;
    };

const wrap = {
  sm: 'wrap',
  md: 'nowrap',
} as const satisfies HorizontalStackProps['wrap'];

interface StatusBarProps {
  actions?: StatusBarAction[];
  icon?: ReactNode;
  title: ReactNode;
  variant?: StatusBarVariant;
}

export function StatusBar({ actions, icon, title, variant = 'text' }: StatusBarProps) {
  return (
    <HorizontalStack align="space-between" blockAlign="center" className={clsx(styles.statusBar, styles[`variant-${variant}`])} gap={4} wrap={wrap}>
      <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
        {icon ? (
          <Typography className={styles.icon} inheritance="current" tone={toneByVariant[variant]}>
            {icon}
          </Typography>
        ) : null}
        <Typography className={styles.title} lineHeight="1rem" size="0.875rem" tone={toneByVariant[variant]}>
          {title}
        </Typography>
      </HorizontalStack>

      {actions?.length ? (
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          {actions.map((action) => {
            if ('node' in action) {
              return <Fragment key={action.id}>{action.node}</Fragment>;
            }

            const baseVariant = variant === 'text' ? 'light-reverse' : 'light';
            const buttonVariant = action.variant ?? baseVariant;

            // CTAs that opt into a solid color variant (e.g. primary/danger) carry their own
            // background, so they keep the button variant's own text tone for contrast (in both
            // light and dark mode) rather than inheriting the banner font color.
            const ctaClassName = action.variant ? undefined : styles.cta;

            const mappedNode = {
              ...action,
              ...match(action)
                .with({ type: 'drawer' }, (a) => ({
                  ...a,
                  drawer: {
                    ...a.drawer,
                    props: {
                      ...a.drawer.props,
                      trigger:
                        action.triggerStyle === 'button' ? (
                          <Button affixIcon={action.ctaIcon} className={ctaClassName} size="small" variant={buttonVariant}>
                            {a.ctaText}
                          </Button>
                        ) : (
                          <TextButton className={ctaClassName}>{a.ctaText}</TextButton>
                        ),
                    },
                  },
                }))
                .with({ type: 'modal' }, (a) => ({
                  ...a,
                  modal: {
                    ...a.modal,
                    props: {
                      ...a.modal.props,
                      trigger:
                        action.triggerStyle === 'button' ? (
                          <Button affixIcon={action.ctaIcon} className={ctaClassName} size="small" variant={buttonVariant}>
                            {a.ctaText}
                          </Button>
                        ) : (
                          <TextButton className={ctaClassName}>{a.ctaText}</TextButton>
                        ),
                    },
                  },
                }))
                .with(
                  { type: 'button' },
                  (a) =>
                    ({
                      ...a,
                      type: 'styledButton',
                      styledButton: {
                        ...a.button,
                        children: a.ctaText,
                        className: ctaClassName,
                        size: 'small',
                        affixIcon: a.ctaIcon,
                        variant: buttonVariant,
                      },
                    }) as const satisfies Interaction,
                )
                .with(
                  { type: 'link', triggerStyle: 'button' },
                  (a) =>
                    ({
                      ...a,
                      type: 'styledButtonLink',
                      styledButtonLink: {
                        ...a.link,
                        children: a.ctaText,
                        className: ctaClassName,
                        size: 'small',
                        affixIcon: a.ctaIcon,
                        to: a.link.to!,
                        variant: buttonVariant,
                      },
                    }) as const satisfies Interaction,
                )
                .otherwise((a) => a),
            };

            return <Interactive key={action.id} {...mappedNode} />;
          })}
        </HorizontalStack>
      ) : null}
    </HorizontalStack>
  );
}
